import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';
import { getCountryFlag } from '../../utils/countryFlags';
import { getTeamLogoUrl } from '../../utils/logoUrl';

interface TeamWithFans {
  id: string;
  name: string;
  shortName?: string;
  logoUrl?: string;
  countryCode?: string;
  competition?: {
    id: string;
    name: string;
    countryCode?: string;
  };
  fanCount: number;
}

const AdminFavouriteTeams: React.FC = () => {
  const [teams, setTeams] = useState<TeamWithFans[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTeamFans = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/admin/team-fans');
        setTeams(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load team fan data');
      } finally {
        setLoading(false);
      }
    };

    fetchTeamFans();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-xl" style={{ color: 'var(--text-primary)' }}>Loading team fan data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-red-600 text-xl">{error}</div>
      </div>
    );
  }

  const totalFans = teams.reduce((sum, team) => sum + team.fanCount, 0);
  const teamsWithFans = teams.filter(team => team.fanCount > 0);
  const teamsWithoutFans = teams.filter(team => team.fanCount === 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Admin Dashboard
          </Link>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Favourite Teams Analytics
          </h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            View which teams are most popular among users
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card rounded-lg shadow p-6 text-center">
            <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {totalFans}
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Total Fans
            </p>
          </div>
          <div className="card rounded-lg shadow p-6 text-center">
            <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {teamsWithFans.length}
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Teams with Fans
            </p>
          </div>
          <div className="card rounded-lg shadow p-6 text-center">
            <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {teamsWithoutFans.length}
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Teams without Fans
            </p>
          </div>
        </div>

        {/* Teams Grid */}
        <div className="card rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>
            Team Fan Counts (Sorted by Most Popular)
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {teams.map((team, index) => (
              <div
                key={team.id}
                className={`card rounded-lg shadow-md p-4 text-center transition-all duration-200 ${
                  team.fanCount > 0 ? 'ring-1 ring-green-500' : ''
                }`}
                style={{ backgroundColor: 'var(--bg-secondary)' }}
              >
                <div className="flex items-center justify-center mb-3">
                  {team.logoUrl ? (
                    <img 
                      src={getTeamLogoUrl(team.logoUrl)} 
                      alt={team.name} 
                      className="w-12 h-12 object-contain"
                    />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center text-2xl">
                      {getCountryFlag(team.countryCode || 'EN')}
                    </div>
                  )}
                </div>
                
                <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                  {team.name}
                </h3>

                
                {team.competition && (
                  <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
                    {team.competition.name}
                  </p>
                )}
                
                <div className="flex items-center justify-center">
                  <span className={`text-lg font-bold ${
                    team.fanCount > 0 ? 'text-green-600' : 'text-gray-400'
                  }`}>
                    {team.fanCount}
                  </span>
                  <span className="text-xs ml-1" style={{ color: 'var(--text-secondary)' }}>
                    {team.fanCount === 1 ? 'fan' : 'fans'}
                  </span>
                </div>
                
                {index < 3 && team.fanCount > 0 && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-highlight text-black">
                      #{index + 1} Most Popular
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFavouriteTeams; 